'use client'

import { useQueryClient } from '@tanstack/react-query'

import { toast } from '@ui/web/components'
import { buildUrl } from '@helper/utils/format'

import type { NotificationTarget } from '../../context/notification-resolver'
import { useAuthClient, useSharedTRPC } from '../../context'

/** The apps that render a notification feed. Each one hosts exactly one organization type. */
export type NotificationHost = 'web' | 'school'

const HOSTED_ORGANIZATION_TYPE: Record<NotificationHost, string> = { web: 'family', school: 'school' }

const HOSTS: readonly NotificationHost[] = ['web', 'school']

/**
 * Makes a route target's organization the active one before the app navigates to it.
 *
 * The feed is per user and the destination is not: a child's profile reads through
 * `myOrganization.getChildDetails`, which is scoped to the session's active organization. A
 * teacher who is also a parent opens their one feed from the school app, where the school is
 * active, and a guardian card about a child who only has a family account points at a profile
 * that organization cannot see — NotFound. Once Sambung Sekolah-Rumah exists notifications are
 * no longer per organization, so the click has to carry the switch.
 *
 * Returns `true` when the caller should push `href` in this app now, `false` when it should do
 * nothing — either the URL was handed to the app that hosts the target's organization (a school
 * app cannot run with a family active: its guard and sidebar assume a school), or the switch
 * failed and the reader has already been told.
 *
 * The switch runs on the click, never on render: a feed can hold cards from several
 * organizations, and the active one must only change when the reader chooses one.
 */
export function useSwitchToNotificationOrganization(host: NotificationHost) {
  const authClient = useAuthClient()
  const trpc = useSharedTRPC()
  const queryClient = useQueryClient()
  const { data: session, refetch: refetchSession } = authClient.useSession()

  return async function switchForTarget(target: NotificationTarget, href: string): Promise<boolean> {
    if (target.kind !== 'route' || !target.organizationId) return true
    if (target.organizationId === session?.session.activeOrganizationId) return true

    // Membership at click time, not at emit time: the snapshot froze the organization when the
    // card was written, and a parent removed from the family since must get a message, not a
    // switch into an organization they can no longer open.
    let organization
    try {
      const organizations = await queryClient.fetchQuery({
        ...trpc.organization.listOrganizations.queryOptions(),
        staleTime: 30_000,
      })
      organization = organizations.find((candidate) => candidate.id === target.organizationId)
    } catch {
      toast.error('Gagal memuat daftar organisasi. Coba lagi, ya.')
      return false
    }

    if (!organization) {
      toast.error('Kamu sudah tidak terhubung dengan keluarga atau sekolah dari pesan ini.')
      return false
    }

    const targetHost = HOSTS.find((candidate) => organization.type.includes(HOSTED_ORGANIZATION_TYPE[candidate]))
    if (!targetHost) {
      toast.error('Pesan ini tidak bisa dibuka dari sini.')
      return false
    }

    const response = await authClient.organization.setActive({ organizationId: organization.id })
    if (response.error) {
      toast.error(`Gagal berpindah ke ${organization.name}.`)
      return false
    }

    if (targetHost === host) {
      // Same app, different organization. Everything cached was read under the old one, and the
      // session hook is what the next click compares against.
      await refetchSession()
      await queryClient.invalidateQueries()
      return true
    }

    // The organization is now active; the other app's guard keeps a valid active organization
    // of its own type (current one first), so it lands on this one rather than re-choosing.
    window.location.assign(buildUrl(targetHost, href).href)
    return false
  }
}
